import { useState } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { If } from 'src/components/If';
import { useAuthContext } from 'src/context/AuthContextProvider';
import { getWalletsByGuestOwner } from 'src/services/wallet';
import { Wallet } from 'src/types/wallet';

import { Filters } from '../Filters';
import { WalletCard } from '../WalletCard';

export function GuestAcceptedWalletsTab({ ownerId }: { ownerId: string }) {
  const { auth } = useAuthContext();
  const [searchName, setSearchName] = useState('');
  const [orderBy, setOrderBy] = useState('');

  const {
    data: wallets,
    isLoading: isLoadingWallets,
    isFetching: isFetchingWallets,
  } = useQuery({
    queryKey: ['accepted-wallets', searchName, orderBy, ownerId],
    queryFn: () =>
      getWalletsByGuestOwner({
        ownerId,
        status: 'accepted',
        filters: {
          name: searchName,
          sort: [orderBy],
        },
      }),
    onError: (error: Error) => {
      toast.error(error.message);
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const isLoadingOrFetchingWallets = isLoadingWallets || isFetchingWallets;

  return (
    <>
      <Filters
        title=""
        searchEntityName="wallet"
        onSearch={(value) => setSearchName(value)}
        onOrderBy={(value) => setOrderBy(value)}
        orderBy={orderBy}
        useCriticalityFilters={false}
      />

      <If condition={isLoadingOrFetchingWallets}>
        <Box
          width="100%"
          minHeight={400}
          display="flex"
          alignItems="center"
          justifyContent="center">
          <CircularProgress color="primary" />
        </Box>
      </If>

      <If condition={!wallets?.length && !isLoadingOrFetchingWallets}>
        <Box
          minHeight={400}
          display="flex"
          alignItems="center"
          justifyContent="center">
          <Typography variant="h5" color="GrayText">
            Não encontramos carteiras
          </Typography>
        </Box>
      </If>

      <If condition={wallets?.length && !isLoadingOrFetchingWallets}>
        <Box
          px={4}
          py={2}
          display="grid"
          gridTemplateColumns={{
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          }}
          flexWrap="wrap"
          alignItems="start"
          gap={1.5}>
          {wallets?.map((wallet: Wallet) => (
            <WalletCard
              key={wallet.id}
              id={wallet.id}
              name={wallet.name}
              fundsCount={wallet.walletFunds.length}
              quotasCount={wallet.walletFunds.reduce(
                (acc, fund) => acc + (fund.quotas?.length || 0),
                0,
              )}
              isSharedWallet={wallet.isSharedWallet}
              useWalletHealthIndicators={false}
              walletButton={{
                label: 'Clique para visualizar',
                redirectTo: `/monitor/${ownerId}/wallet/${wallet.id}`,
              }}
            />
          ))}
        </Box>
      </If>
    </>
  );
}
